/**
 * BossOverview - Dashboard Boss Ton Concours
 * Stats : examens, quiz, abonnements, challenges, leaderboard
 */
import {
  Award,
  BookOpen,
  CreditCard,
  Flame,
  Loader2,
  RefreshCw,
  Star,
  Target,
  Trophy,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface BossStats {
  total_users: number;
  premium_users: number;
  active_subscriptions: number;
  total_quizzes_done: number;
  daily_challenges_today: number;
  lessons_completed_7d: number;
  badges_distributed: number;
  total_exams: number;
  total_questions: number;
  current_streak_avg: number;
}

interface LeaderboardEntry {
  rank: number;
  full_name: string;
  total_points: number;
  current_streak: number;
  is_premium: boolean;
}

const KpiCard = ({
  icon,
  label,
  value,
  sub,
  color = 'blue',
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}) => {
  const colorMap: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg ${colorMap[color] ?? colorMap.blue}`}>{icon}</div>
      </div>
    </div>
  );
};

interface BossOverviewProps {
  onNavigate?: (section: string) => void;
}

const BossOverview: React.FC<BossOverviewProps> = ({ onNavigate }) => {
  const [stats, setStats] = useState<BossStats | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, lbRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/boss/stats`),
        authFetch(`${API_BASE_URL}/api/boss/leaderboard?limit=5`),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (lbRes.ok) setLeaderboard((await lbRes.json()).entries ?? []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const s: BossStats = stats ?? {
    total_users: 0, premium_users: 0, active_subscriptions: 0,
    total_quizzes_done: 0, daily_challenges_today: 0, lessons_completed_7d: 0,
    badges_distributed: 0, total_exams: 0, total_questions: 0, current_streak_avg: 0,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-5 h-5 text-blue-500" />
            <h1 className="text-2xl font-bold text-gray-900">Vue d'ensemble Boss Ton Concours</h1>
          </div>
          <p className="text-gray-500 text-sm">Plateforme d'entraînement aux concours — quiz, leçons, gamification</p>
        </div>
        <button onClick={loadData} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Product badge */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <BookOpen className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800 mb-1">Boss Ton Concours — Entraînement intensif</h3>
            <p className="text-xs text-blue-600">
              Application axée sur la <strong>préparation aux concours</strong> via quiz interactifs, cours structurés et gamification (badges, streak, classement).
              Modèle d'abonnement mensuel : <strong>Free → Single → Multi → Unlimited</strong>.
              Différent de KPA Ton Concours (recherche de concours + IA).
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
          ⚠️ API Boss non encore créée — créer <code className="bg-blue-100 px-1 rounded">/api/boss/stats</code> dans le backend
        </div>
      )}

      {/* KPIs — Utilisateurs */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Utilisateurs</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={<Users className="w-4 h-4" />} label="Total inscrits" value={s.total_users} color="blue" />
          <KpiCard icon={<CreditCard className="w-4 h-4" />} label="Abonnés premium" value={s.premium_users} sub={`${s.active_subscriptions} actifs`} color="indigo" />
          <KpiCard icon={<Flame className="w-4 h-4" />} label="Streak moyen (jours)" value={s.current_streak_avg} color="amber" />
          <KpiCard icon={<Award className="w-4 h-4" />} label="Badges distribués" value={s.badges_distributed} color="purple" />
        </div>
      </div>

      {/* KPIs — Activité */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Activité</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={<Target className="w-4 h-4" />} label="Quiz effectués (total)" value={s.total_quizzes_done} color="blue" />
          <KpiCard icon={<Flame className="w-4 h-4" />} label="Challenges du jour" value={s.daily_challenges_today} color="rose" />
          <KpiCard icon={<BookOpen className="w-4 h-4" />} label="Leçons terminées 7j" value={s.lessons_completed_7d} color="emerald" />
          <KpiCard icon={<Star className="w-4 h-4" />} label="Questions dans la BDD" value={s.total_questions} sub={`${s.total_exams} examens`} color="amber" />
        </div>
      </div>

      {/* Leaderboard */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" />
          <h2 className="font-semibold text-gray-800">Top 5 Classement</h2>
        </div>
        {leaderboard.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Trophy className="w-8 h-8 mx-auto mb-2" />
            <p>Classement vide ou API non disponible</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {leaderboard.map((entry) => (
              <div key={entry.rank} className="flex items-center gap-4 px-4 py-3">
                <span className="text-lg font-bold text-gray-300 w-6">
                  {entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : entry.rank}
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 text-sm">{entry.full_name}</p>
                    {entry.is_premium && (
                      <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">Premium</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-amber-500">🔥 {entry.current_streak}j</span>
                  </div>
                </div>
                <span className="font-bold text-blue-600">{entry.total_points.toLocaleString()} pts</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Accès rapides */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Accès rapides</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Générateur de questions', icon: Star, section: 'questions', color: 'from-blue-500 to-indigo-600' },
            { label: 'Leçons & cours', icon: BookOpen, section: 'learning', color: 'from-indigo-500 to-purple-600' },
            { label: 'Abonnements', icon: CreditCard, section: 'payments', color: 'from-green-500 to-emerald-600' },
            { label: 'Utilisateurs Boss', icon: Users, section: 'boss-users', color: 'from-sky-500 to-blue-600' },
          ].map(({ label, icon: Icon, section, color }) => (
            <button
              key={label}
              onClick={() => onNavigate?.(section)}
              className={`bg-gradient-to-br ${color} text-white rounded-xl p-4 text-left hover:opacity-90 transition`}
            >
              <Icon className="w-5 h-5 mb-2" />
              <p className="text-sm font-medium">{label}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BossOverview;
